
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useAggregatedDownload = (collectionId: string) => {
  const { data: isEligible, isLoading } = useQuery({
    queryKey: ["aggregated-download-eligibility", collectionId],
    queryFn: async () => {
      if (!collectionId) return false;

      console.log('Checking aggregated download eligibility for collection:', collectionId);

      // Check if collection has approved products
      const { data: products, error: productsError } = await supabase
        .from("product_descriptions")
        .select("id, status")
        .eq("collection_id", collectionId);

      if (productsError) {
        console.error('Error fetching products:', productsError);
        throw productsError;
      }

      console.log('Products found:', products?.length);
      const approvedProducts = products?.filter(p => p.status === 'approved') || [];
      console.log('Approved products:', approvedProducts.length);
      
      if (approvedProducts.length === 0) {
        console.log('No approved products found');
        return false;
      }

      // Check if there are any translations for this collection
      const { data: translations, error: translationsError } = await supabase
        .from("translations")
        .select("id, language_code")
        .eq("collection_id", collectionId);

      if (translationsError) {
        console.error('Error fetching translations:', translationsError);
        throw translationsError;
      }

      console.log('Translations found:', translations?.length);

      // If no translations exist, not eligible for aggregated download
      if (!translations || translations.length === 0) {
        console.log('No translations found');
        return false;
      }

      // Check if ALL translations have all their descriptions approved
      let allTranslationsFullyApproved = true;
      
      for (const translation of translations) {
        console.log(`Checking translation ${translation.id} (${translation.language_code})`);
        
        // Get all translated descriptions for this translation
        const { data: translatedDescriptions, error: descriptionsError } = await supabase
          .from("translated_descriptions")
          .select("id, status")
          .eq("translation_id", translation.id);

        if (descriptionsError) {
          console.error(`Error fetching translated descriptions for ${translation.id}:`, descriptionsError);
          throw descriptionsError;
        }

        console.log(`Translation ${translation.language_code} has ${translatedDescriptions?.length || 0} descriptions`);

        // If no descriptions exist for this translation, it's not fully approved
        if (!translatedDescriptions || translatedDescriptions.length === 0) {
          console.log(`No translated descriptions found for ${translation.language_code} - marking as not fully approved`);
          allTranslationsFullyApproved = false;
          continue;
        }

        // Check if all descriptions in this translation are approved
        const approvedCount = translatedDescriptions.filter(desc => desc.status === 'approved').length;
        const allApproved = approvedCount === translatedDescriptions.length;
        
        console.log(`Translation ${translation.language_code}: ${approvedCount}/${translatedDescriptions.length} descriptions approved (all approved: ${allApproved})`);
        
        if (!allApproved) {
          allTranslationsFullyApproved = false;
          console.log(`Translation ${translation.language_code} is not fully approved - aggregated download not eligible`);
        } else {
          console.log(`Translation ${translation.language_code} is fully approved`);
        }
      }

      console.log('All translations fully approved:', allTranslationsFullyApproved);
      return allTranslationsFullyApproved;
    },
    enabled: !!collectionId,
    refetchInterval: 5000, // Refresh every 5 seconds to stay updated
  });

  return {
    isEligible: isEligible || false,
    isLoading
  };
};
